import type { AxiosInstance } from 'axios'
import type { UploadFile } from 'antd'
import type { TFunction } from 'i18next'

import { ATTACHMENT_MAX_FILES, attachmentProblem } from '../constants/ticketAttachments'

// mergeAttachments appends the acceptable files of `incoming` to `prev` and
// reports the rejected ones through `notify` (customer-facing text, translated).
// Checks mirror the server limits so a customer learns about a 11 MB file or a
// 6th attachment right when adding it, not after the upload failed.
export function mergeAttachments(
  prev: UploadFile[],
  incoming: UploadFile[],
  t: TFunction,
  notify: (text: string) => void,
): UploadFile[] {
  const next = [...prev]
  for (const f of incoming) {
    const file = f.originFileObj ?? f
    const problem = attachmentProblem({ size: file.size, type: file.type })
    if (problem === 'tooLarge') {
      notify(t('portal.tickets.attach.tooLarge', '{{name}} ist größer als 10 MB und wurde nicht angehängt.', { name: f.name }))
    } else if (problem === 'badType') {
      notify(t('portal.tickets.attach.badType', '{{name}}: Dateityp nicht erlaubt.', { name: f.name }))
    } else if (next.length >= ATTACHMENT_MAX_FILES) {
      notify(t('portal.tickets.attach.tooMany', 'Maximal {{max}} Anhänge pro Ticket.', { max: ATTACHMENT_MAX_FILES }))
      break
    } else {
      next.push(f)
    }
  }
  return next
}

// uploadMessageAttachments uploads files to an existing message and returns how
// many failed. It NEVER throws: the message already exists server-side, so
// reporting the whole action as failed would invite a resend and a duplicate
// message. The caller warns instead and points to the ticket view, where files
// can be added later. `base` is the ticket API root (e.g. /api/v1/portal/tickets).
export async function uploadMessageAttachments(
  api: Pick<AxiosInstance, 'post'>,
  base: string,
  ticketID: number,
  msgID: number,
  files: File[],
): Promise<number> {
  let failed = 0
  for (const file of files) {
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.post(`${base}/${ticketID}/messages/${msgID}/attachments`, fd)
    } catch {
      failed++
    }
  }
  return failed
}

// uploadOpeningAttachments does the same for the opening message of a freshly
// created ticket, whose id first has to be read back from the ticket.
export async function uploadOpeningAttachments(
  api: Pick<AxiosInstance, 'get' | 'post'>,
  base: string,
  ticketID: number,
  files: File[],
): Promise<number> {
  if (files.length === 0) return 0
  let msgID: number | undefined
  try {
    const detail = await api.get<{ data: { messages: { id: number }[] } }>(`${base}/${ticketID}`)
    msgID = detail.data.data.messages[0]?.id
  } catch {
    return files.length
  }
  if (!msgID) return files.length
  return uploadMessageAttachments(api, base, ticketID, msgID, files)
}
